// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ApplicationPanelWithTable } from '../ApplicationPanelWithTable';
import ObjectManagerBaseTemplate from '@front/templates/ObjectManagerBaseTemplate.html';
import { DynProperty } from '@shared/commons/DynProperty';
import { TerceraChartAction } from '@front/chart/TerceraChartAction';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class ObjectManagerPanelBase extends ApplicationPanelWithTable {
    get Chart() {
        return this._chart;
    }
    set Chart(value) {
        this.unsubscribe();
        this._chart = value;
        this.subscribe();
        this.repopulate();
    }
    constructor() {
        super();
        this._chart = null;
    }
    repopulate() {
        const qt = this.getQuickTable();
        if (!isNullOrUndefined(qt)) {
            qt.ClearAll();
            this.populateItemsDirect();
        }
    }
    subscribe() { }
    unsubscribe() { }
    oninit() {
        super.oninit();
        void this.set('isRelativeStyle', true);
        this.observe('visible', this.onVisibleChanged);
    }
    oncomplete() {
        super.oncomplete();
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt)) {
            return;
        }
        const menuItems = this.createMenuItems();
        this.menuTagDict = createTagDictionary(menuItems);
        qt.setTableContextMenuItems(menuItems);
        qt.OnSelectionChanged.Subscribe(this.selectionChange, this);
        qt.OnPaintedPictureButtonClick.Subscribe(this.onPictureButtonClick, this);
    }
    dispose() {
        this.unsubscribe();
        super.dispose();
    }
    TickAsync() {
        super.TickAsync();
        const isVisible = this.get('visible');
        if (!isVisible) {
            return null;
        }
        this.updateAllowedActionsRemoveAll();
        this.getQuickTable().needRedrawBackground = true;
    }
    RemoveItemFromTable(itemSubject) {
        const quickTable = super.getQuickTable();
        if (isNullOrUndefined(quickTable)) {
            return;
        }
        for (const row of quickTable.rowsArray) {
            if (row.item.getItemSubject() === itemSubject) {
                quickTable.RemoveItem(row.item.ItemId);
                break;
            }
        }
    }
    createMenuItems() {
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: 'chart.menu.ObjectManager.RemoveSelected',
                tag: MenuActionEnum.RemoveSelected,
                enabled: false,
                event: this.menuItemAction.bind(this, MenuActionEnum.RemoveSelected)
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'chart.menu.ObjectManager.RemoveAll',
                tag: MenuActionEnum.RemoveAll,
                event: this.menuItemAction.bind(this, MenuActionEnum.RemoveAll)
            }
        ];
        return items;
    }
    menuItemAction(action) {
        switch (action) {
            case MenuActionEnum.RemoveSelected:
                this.menuActionRemoveSelected(this.getSelectedRowItems());
                break;
            case MenuActionEnum.RemoveAll:
                this.menuActionRemoveAll();
                break;
        }
    }
    getSelectedRowItems() {
        const qt = this.getQuickTable();
        const items = [];
        for (const id of qt.selectedRowIds) {
            items.push(qt.rows[id].item);
        }
        return items;
    }
    onVisibleChanged() {
        this.layoutTable();
    }
    selectionChange() {
        const qt = this.getQuickTable();
        const selectedRowsId = qt.selectedRowIds;
        if (isValidArray(selectedRowsId)) {
            this.updateAllowedActionsRemoveSelected(true);
        }
        else {
            this.updateAllowedActionsRemoveSelected(false);
        }
    }
    updateAllowedActionsRemoveSelected(enabled) {
        const menuTagDict = this.menuTagDict;
        if (isNullOrUndefined(menuTagDict)) {
            return;
        }
        menuTagDict[MenuActionEnum.RemoveSelected].enabled = enabled;
    }
    updateAllowedActionsRemoveAll() {
        const menuTagDict = this.menuTagDict;
        if (isNullOrUndefined(menuTagDict)) {
            return;
        }
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt)) {
            return;
        }
        const rowsArray = qt.rowsArray;
        if (isValidArray(rowsArray)) {
            menuTagDict[MenuActionEnum.RemoveAll].enabled = true;
        }
        else {
            menuTagDict[MenuActionEnum.RemoveAll].enabled = false;
        }
    }
    onPictureButtonClick(args) {
        if (isNullOrUndefined(args)) {
            return;
        }
        const item = args.row.item;
        switch (args.controlType) {
            case DynProperty.VISIBILITY_BUTTON:
                this.processItemVisibility(item);
                break;
            case DynProperty.SETTINGS_BUTTON:
                this.processSettingsOpen(item);
                break;
            case DynProperty.CLOSE_BUTTON:
                this.processCloseItem(item);
                break;
            case DynProperty.SHOW_ON_CHART_BUTTON:
                this.processShowOnChartItem(item);
                break;
        }
        this.getQuickTable().redraw();
    }
    processItemVisibility(item) {
        if (!isNullOrUndefined(item) && !isNullOrUndefined(this.Chart)) {
            this.Chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.ChartActionVisibility, item.getItemSubject()));
        }
    }
    processSettingsOpen(item) {
        if (!isNullOrUndefined(item) && !isNullOrUndefined(this.Chart)) {
            this.Chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.ChartActionSettings, item.getItemSubject()));
        }
    }
    processCloseItem(item) {
        if (!isNullOrUndefined(item) && !isNullOrUndefined(this.Chart)) {
            this.Chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.ChartActionRemove, item.getItemSubject()));
        }
    }
    processShowOnChartItem(item) {
        if (!isNullOrUndefined(item) && !isNullOrUndefined(this.Chart)) {
            this.Chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.ChartActionShowOnChart, item.getItemSubject()));
        }
    }
    menuActionRemoveSelected(items) {
        for (const item of items) {
            this.processCloseItem(item);
        }
    }
    menuActionRemoveAll() {
        if (!isNullOrUndefined(this.Chart)) {
            this.Chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.ChartActionDeleteAll, true));
        }
    }
}
ApplicationPanelWithTable.extendWith(ObjectManagerPanelBase, {
    data: function () {
        return {
            dockablePanel: false
        };
    },
    partials: {
        bodyPartial: ObjectManagerBaseTemplate
    }
});
var MenuActionEnum;
(function (MenuActionEnum) {
    MenuActionEnum[MenuActionEnum["RemoveSelected"] = 0] = "RemoveSelected";
    MenuActionEnum[MenuActionEnum["RemoveAll"] = 1] = "RemoveAll";
})(MenuActionEnum || (MenuActionEnum = {}));
